import React from 'react';
import { MicrophoneIcon, PauseIcon, PlayIcon, StopIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useRecording, RecordingSession } from '../contexts/RecordingContext';

const HeaderRecordingDropdown = () => {
  const { sessions, activeSessionId, actions, utils } = useRecording();

  const activeSession = activeSessionId ? sessions[activeSessionId] : null;
  const pausedSessions = Object.values(sessions).filter(s => s.status === 'paused');
  const uploadingSessions = Object.values(sessions).filter(s => s.status === 'uploading');
  const hasAnySessions = activeSession || pausedSessions.length > 0 || uploadingSessions.length > 0;

  let displaySession: RecordingSession | null = activeSession;
  if (!displaySession && pausedSessions.length > 0) {
    displaySession = [...pausedSessions].sort(
      (a, b) => (b.segmentStartTs || b.createdAt) - (a.segmentStartTs || a.createdAt)
    )[0];
  }
  const triggerDuration = displaySession ? utils.formatDuration(utils.computeDurationMs(displaySession)) : null;
  const firstName = (displaySession?.patientName || '').trim().split(/\s+/)[0] || '';

  const [open, setOpen] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);
  const [enter, setEnter] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as Element)?.closest?.('[data-rec-dropdown-root]')) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setShouldRender(true);
      setEnter(false);
      const rAF = requestAnimationFrame(() => setEnter(true));
      return () => cancelAnimationFrame(rAF);
    } else if (shouldRender) {
      setEnter(false);
      const t = setTimeout(() => setShouldRender(false), 320);
      return () => clearTimeout(t);
    }
  }, [open]);

  React.useEffect(() => {
    if (!hasAnySessions) setOpen(false);
  }, [hasAnySessions]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div className='relative' data-rec-dropdown-root>
      <button
        type='button'
        aria-haspopup='menu'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={`group flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-sm font-medium transition shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent ${
          activeSession
            ? 'bg-red-600 text-white'
            : displaySession
            ? 'bg-amber-500 text-white'
            : 'bg-brand-navy text-white hover:bg-brand-navy/90'
        }`}
      >
        {displaySession ? (
          <span className='flex items-center gap-2 max-w-[18rem] leading-none'>
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${activeSession ? 'bg-white animate-pulse' : 'bg-white/80'}`} />
            <span className='truncate text-[15px] font-semibold leading-none opacity-95'>
              {firstName ? `${firstName.toLowerCase()}:` : (displaySession?.patientId ?? '')}
            </span>
            <span className='font-mono tabular-nums tracking-tight text-[16px] font-semibold flex-shrink-0'>
              {triggerDuration}
            </span>
          </span>
        ) : (
          <span className='flex items-center gap-2'>
            <MicrophoneIcon className='h-4 w-4 text-white' />
            <span className='text-xs font-medium text-white'>Record</span>
          </span>
        )}
      </button>

      {shouldRender && (
        <div
          role='menu'
          aria-label='Recording sessions'
          aria-hidden={!open}
          className={`absolute right-0 mt-2 w-80 bg-white border border-brand-navy/15 ring-1 ring-brand-navy/15 rounded-xl shadow-lg p-2.5 z-50 origin-top-right transform transition-all duration-[260ms] ease-out ${
            enter ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
          }`}
        >
          {activeSession && (
            <RecSection title='Active' accent='text-red-600'>
              <RecCard session={activeSession} isActive actions={actions} utils={utils} />
            </RecSection>
          )}
          {pausedSessions.length > 0 && (
            <RecSection title='Paused' accent='text-amber-600'>
              {pausedSessions.map(s => (
                <RecCard key={s.session_id} session={s} actions={actions} utils={utils} />
              ))}
            </RecSection>
          )}
          {uploadingSessions.length > 0 && (
            <RecSection title='Uploading' accent='text-blue-600'>
              {uploadingSessions.map(s => (
                <div key={s.session_id} className='px-3 py-2 rounded-lg border border-brand-navy/15 bg-white text-xs text-gray-500 flex items-center gap-2'>
                  <span className='w-2 h-2 rounded-full bg-blue-500 animate-pulse' />
                  {s.patientName || s.patientId} — uploading…
                </div>
              ))}
            </RecSection>
          )}
          {!hasAnySessions && (
            <div className='py-8 flex flex-col items-center text-center gap-2 text-gray-500 text-xs'>
              <MicrophoneIcon className='h-8 w-8 text-gray-300' />
              <p>No active recordings</p>
              <p className='text-[10px] text-gray-400'>Start a recording from a patient page.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RecSection = ({ title, accent, children }: { title: string; accent: string; children: React.ReactNode }) => (
  <div className='mb-3 last:mb-0'>
    <div className='flex items-center gap-2 mb-1'>
      <h3 className={`text-[10px] font-semibold uppercase tracking-wide ${accent}`}>{title}</h3>
      <div className='flex-1 h-px bg-gray-100' />
    </div>
    <div className='space-y-1.5'>{children}</div>
  </div>
);

const RecAvatar = ({ name }: { name: string }) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';
  return (
    <div className='h-8 w-8 rounded-full bg-gradient-to-br from-brand-navy to-brand-accent text-white flex items-center justify-center text-[11px] font-semibold shadow-inner select-none'>
      {initials}
    </div>
  );
};

interface RecCardProps {
  session: RecordingSession;
  isActive?: boolean;
  actions: ReturnType<typeof useRecording>['actions'];
  utils: ReturnType<typeof useRecording>['utils'];
}

const RecCard = ({ session, isActive = false, actions, utils }: RecCardProps) => {
  const duration = utils.formatDuration(utils.computeDurationMs(session));
  return (
    <div className={`px-3 py-2 rounded-lg border shadow-sm ${isActive ? 'border-red-200 bg-red-50/70' : 'border-brand-navy/15 bg-white'}`}>
      <div className='flex items-start gap-3'>
        <RecAvatar name={session.patientName || session.patientId} />
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='flex-1 min-w-0 text-[15px] font-semibold leading-tight truncate'>
              {session.patientName || session.patientId}
            </span>
            {isActive && (
              <span className='px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700'>LIVE</span>
            )}
            {!isActive && session.status === 'paused' && (
              <span className='px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700'>PAUSED</span>
            )}
            <span className='flex items-center gap-1 font-mono tabular-nums text-[15px] font-semibold ml-auto'>
              <span className={`w-2 h-2 rounded-full ${session.status === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-amber-400'}`} />
              <span className={session.status === 'recording' ? 'text-red-700' : 'text-gray-600'}>{duration}</span>
            </span>
          </div>
          <div className='mt-2 flex items-center gap-2'>
            {session.status === 'recording' && (
              <RecBtn label='Pause' tone='bg-amber-500' onClick={() => actions.pauseRecording(session.session_id)}>
                <PauseIcon className='h-4 w-4' />
              </RecBtn>
            )}
            {session.status === 'paused' && (
              <RecBtn label='Resume' tone='bg-green-600' onClick={() => actions.resumeRecording(session.session_id)}>
                <PlayIcon className='h-4 w-4' />
              </RecBtn>
            )}
            <RecBtn label='Stop & Upload' tone='bg-brand-navy' onClick={() => actions.stopRecording(session.session_id)}>
              <StopIcon className='h-4 w-4' />
            </RecBtn>
            <RecBtn label='Discard' tone='bg-gray-400' onClick={() => actions.discardSession(session.session_id)}>
              <TrashIcon className='h-4 w-4' />
            </RecBtn>
          </div>
        </div>
      </div>
    </div>
  );
};

const RecBtn = ({ onClick, label, children, tone }: { onClick: () => void; label: string; children: React.ReactNode; tone: string }) => (
  <button
    role='menuitem'
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`h-7 w-7 rounded-md flex items-center justify-center text-white ${tone} hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand-accent`}
  >
    {children}
  </button>
);

export default HeaderRecordingDropdown;
